import React, { useState } from 'react';
import {
  Users, Shield, TrendingUp, TrendingDown, Minus, AlertTriangle,
  CheckCircle, XCircle, Clock, Activity, ChevronRight, Key, Laptop,
  Cloud, Lock, Wifi, DollarSign, RotateCcw
} from 'lucide-react';

const agents = [
  {
    id: "identity_agent",
    name: "Identity Agent",
    domain: "Identity",
    icon: Key,
    status: "active",
    version: "2.4.1",
    rolloutStatus: "stable",
    capabilities: ["user.create", "user.deactivate", "group.manage", "access.grant", "access.revoke"],
    allowedActionClasses: ["standard", "sensitive"],
    trust: {
      score: 98.2,
      trend: "up",
      drivers: [
        { factor: "Successful actions", value: "94.4%", impact: "positive" },
        { factor: "Rollback rate", value: "0.6%", impact: "positive" },
        { factor: "Policy compliance", value: "100%", impact: "positive" },
        { factor: "Verification pass rate", value: "99.1%", impact: "positive" }
      ]
    },
    metrics: {
      successRate: 99.4,
      rollbackRate: 0.6,
      avgCostPerCase: 0.82,
      actionsLast24h: 47,
      casesLast24h: 12
    },
    last7dBlocks: [
      { reason: "Budget exceeded", count: 1, example: "Premium license request over $500" },
      { reason: "Missing approval", count: 2, example: "Admin access without manager sign-off" }
    ],
    connectors: ["okta", "entra_id"],
    lastUpdated: "2026-01-15T08:00:00Z"
  },
  {
    id: "endpoint_agent",
    name: "Endpoint Agent",
    domain: "Endpoints",
    icon: Laptop,
    status: "active",
    version: "2.3.0",
    rolloutStatus: "stable",
    capabilities: ["device.enroll", "device.wipe", "device.quarantine", "policy.apply", "app.deploy"],
    allowedActionClasses: ["standard", "sensitive", "destructive"],
    trust: {
      score: 97.8,
      trend: "stable",
      drivers: [
        { factor: "Successful actions", value: "96.1%", impact: "positive" },
        { factor: "Rollback rate", value: "2.6%", impact: "neutral" },
        { factor: "Policy compliance", value: "100%", impact: "positive" },
        { factor: "Device health impact", value: "98.5%", impact: "positive" }
      ]
    },
    metrics: {
      successRate: 97.4,
      rollbackRate: 2.6,
      avgCostPerCase: 1.15,
      actionsLast24h: 23,
      casesLast24h: 8
    },
    last7dBlocks: [
      { reason: "High blast radius", count: 3, example: "Bulk wipe request for 50+ devices" },
      { reason: "Canary failure", count: 1, example: "Patch deployment failed on test group" }
    ],
    connectors: ["intune", "jamf"],
    lastUpdated: "2026-01-15T07:30:00Z"
  },
  {
    id: "saas_agent",
    name: "SaaS Agent",
    domain: "SaaS",
    icon: Cloud,
    status: "degraded",
    version: "2.2.5",
    rolloutStatus: "canary",
    capabilities: ["user.provision", "user.deprovision", "license.assign", "license.reclaim", "app.configure"],
    allowedActionClasses: ["standard"],
    trust: {
      score: 94.1,
      trend: "down",
      drivers: [
        { factor: "Successful actions", value: "94.4%", impact: "neutral" },
        { factor: "Rollback rate", value: "5.6%", impact: "negative" },
        { factor: "Slack connector latency", value: "+40%", impact: "negative" },
        { factor: "Cost efficiency", value: "$0.12/action", impact: "positive" }
      ]
    },
    metrics: {
      successRate: 94.4,
      rollbackRate: 5.6,
      avgCostPerCase: 0.68,
      actionsLast24h: 31,
      casesLast24h: 15
    },
    last7dBlocks: [
      { reason: "Connector timeout", count: 5, example: "Slack API rate limit exceeded" },
      { reason: "License limit", count: 2, example: "Figma seats at capacity" }
    ],
    connectors: ["google_workspace", "slack", "github", "figma", "salesforce"],
    lastUpdated: "2026-01-15T09:00:00Z",
    alert: "Slack connector latency increased 40%. Investigating."
  },
  {
    id: "network_agent",
    name: "Network Agent",
    domain: "Network",
    icon: Wifi,
    status: "active",
    version: "1.8.2",
    rolloutStatus: "stable",
    capabilities: ["vpn.provision", "firewall.rule", "dns.manage", "certificate.rotate"],
    allowedActionClasses: ["standard"],
    trust: {
      score: 99.1,
      trend: "up",
      drivers: [
        { factor: "Successful actions", value: "99.5%", impact: "positive" },
        { factor: "Rollback rate", value: "0%", impact: "positive" },
        { factor: "Policy compliance", value: "100%", impact: "positive" }
      ]
    },
    metrics: {
      successRate: 99.5,
      rollbackRate: 0,
      avgCostPerCase: 0.45,
      actionsLast24h: 8,
      casesLast24h: 3
    },
    last7dBlocks: [],
    connectors: ["cloudflare", "palo_alto"],
    lastUpdated: "2026-01-15T06:00:00Z"
  },
  {
    id: "security_agent",
    name: "Security Agent",
    domain: "Security",
    icon: Lock,
    status: "active",
    version: "3.1.0",
    rolloutStatus: "stable",
    capabilities: ["threat.contain", "access.emergency_revoke", "evidence.package", "incident.respond"],
    allowedActionClasses: ["standard", "sensitive", "emergency"],
    trust: {
      score: 99.5,
      trend: "stable",
      drivers: [
        { factor: "Successful actions", value: "100%", impact: "positive" },
        { factor: "Rollback rate", value: "0%", impact: "positive" },
        { factor: "Containment speed", value: "< 30s", impact: "positive" },
        { factor: "False positive rate", value: "1.2%", impact: "positive" }
      ]
    },
    metrics: {
      successRate: 100,
      rollbackRate: 0,
      avgCostPerCase: 2.30,
      actionsLast24h: 12,
      casesLast24h: 4
    },
    last7dBlocks: [
      { reason: "Manual escalation required", count: 1, example: "Cross-tenant data access attempt" }
    ],
    connectors: ["crowdstrike", "sentinel"],
    lastUpdated: "2026-01-15T09:15:00Z"
  }
];

const TrustTrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="w-4 h-4 text-primary" />;
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-slate-900" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

const getStatusBadge = (status) => {
  const config = {
    active: 'badge-active',
    degraded: 'badge-pending',
    inactive: 'badge-failed'
  };
  const labels = {
    active: 'Active',
    degraded: 'Degraded',
    inactive: 'Inactive'
  };
  return { className: config[status] || 'badge-neutral', label: labels[status] || status };
};

export const AgentFleet = () => {
  const [selectedAgent, setSelectedAgent] = useState(agents[0]);

  const totalActions = agents.reduce((sum, a) => sum + a.metrics.actionsLast24h, 0);
  const avgTrust = (agents.reduce((sum, a) => sum + a.trust.score, 0) / agents.length).toFixed(1);
  const totalCost = agents.reduce((sum, a) => sum + (a.metrics.avgCostPerCase * a.metrics.casesLast24h), 0).toFixed(2);

  return (
    <div className="space-y-6" data-testid="agent-fleet-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-500" />
            Agent Fleet
          </h1>
          <p className="text-sm text-slate-500 mt-1">Capabilities, trust drivers, and performance metrics</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Agents</span>
            <Users className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{agents.length}</div>
          <div className="text-xs text-slate-500 mt-1">{agents.filter(a => a.status === 'active').length} active</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Avg Trust</span>
            <Shield className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{avgTrust}%</div>
          <div className="text-xs text-primary mt-1">Target: &gt; 95%</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Actions (24h)</span>
            <Activity className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{totalActions}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Cost (24h)</span>
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">${totalCost}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Agents</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {agents.map((agent) => {
                const statusBadge = getStatusBadge(agent.status);
                return (
                  <button
                    key={agent.id}
                    onClick={() => setSelectedAgent(agent)}
                    className={`w-full px-4 py-3 text-left transition-colors ${
                      selectedAgent?.id === agent.id ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        agent.status === 'active' ? 'bg-primary/10' : 'bg-slate-200'
                      }`}>
                        <agent.icon className={`w-5 h-5 ${
                          agent.status === 'active' ? 'text-primary' : 'text-slate-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">{agent.domain}</span>
                          <span className={`badge-status ${statusBadge.className}`}>{statusBadge.label}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-500">{agent.trust.score}% trust</span>
                          <TrustTrendIcon trend={agent.trust.trend} />
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {selectedAgent && (
            <>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      selectedAgent.status === 'active' ? 'bg-primary/10' : 'bg-slate-200'
                    }`}>
                      <selectedAgent.icon className={`w-6 h-6 ${
                        selectedAgent.status === 'active' ? 'text-primary' : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-slate-900">{selectedAgent.name}</h2>
                        <span className={`badge-status ${getStatusBadge(selectedAgent.status).className}`}>
                          {getStatusBadge(selectedAgent.status).label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span>v{selectedAgent.version}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${
                          selectedAgent.rolloutStatus === 'stable' ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-700'
                        }`}>
                          {selectedAgent.rolloutStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-slate-900">{selectedAgent.trust.score}%</div>
                    <div className="flex items-center justify-end gap-1 text-sm text-slate-500">
                      Trust Score
                      <TrustTrendIcon trend={selectedAgent.trust.trend} />
                    </div>
                  </div>
                </div>

                {selectedAgent.alert && (
                  <div className="mt-4 p-3 bg-slate-100 border border-slate-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-slate-600 mt-0.5" />
                    <span className="text-sm text-slate-700">{selectedAgent.alert}</span>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Trust Drivers</h3>
                <div className="space-y-3">
                  {selectedAgent.trust.drivers.map((driver, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {driver.impact === 'positive' ? (
                          <CheckCircle className="w-4 h-4 text-primary" />
                        ) : driver.impact === 'negative' ? (
                          <XCircle className="w-4 h-4 text-slate-900" />
                        ) : (
                          <Clock className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="text-sm text-slate-700">{driver.factor}</span>
                      </div>
                      <span className={`text-sm font-medium ${
                        driver.impact === 'positive' ? 'text-primary' :
                        driver.impact === 'negative' ? 'text-slate-900' : 'text-slate-600'
                      }`}>
                        {driver.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-xs text-slate-500 mb-1">Success Rate</div>
                  <div className="text-xl font-semibold text-slate-900">{selectedAgent.metrics.successRate}%</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-xs text-slate-500 mb-1">Rollback Rate</div>
                  <div className="text-xl font-semibold text-slate-900">{selectedAgent.metrics.rollbackRate}%</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-xs text-slate-500 mb-1">Avg Cost/Case</div>
                  <div className="text-xl font-semibold text-slate-900">${selectedAgent.metrics.avgCostPerCase}</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-xs text-slate-500 mb-1">Actions (24h)</div>
                  <div className="text-xl font-semibold text-slate-900">{selectedAgent.metrics.actionsLast24h}</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-xs text-slate-500 mb-1">Cases (24h)</div>
                  <div className="text-xl font-semibold text-slate-900">{selectedAgent.metrics.casesLast24h}</div>
                </div>
              </div>

              {selectedAgent.last7dBlocks.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <RotateCcw className="w-4 h-4 text-slate-400" />
                    Blocks (Last 7 Days)
                  </h3>
                  <div className="space-y-3">
                    {selectedAgent.last7dBlocks.map((block, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-xs font-medium text-white">
                          {block.count}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{block.reason}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Example: {block.example}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Capabilities</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedAgent.capabilities.map((cap, idx) => (
                      <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-lg">
                        {cap}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="text-xs text-slate-500 mb-2">Allowed Action Classes</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedAgent.allowedActionClasses.map((cls, idx) => (
                        <span key={idx} className={`px-2 py-1 text-xs rounded-lg ${
                          cls === 'standard' ? 'bg-primary/10 text-primary' :
                          cls === 'sensitive' ? 'bg-slate-200 text-slate-700' :
                          cls === 'destructive' ? 'bg-slate-900 text-white' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {cls}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Connectors</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedAgent.connectors.map((conn, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-lg">
                        {conn}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentFleet;
